import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Spinner,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';

import { Order, OrderDetail, OrderStatus } from '@app/api/models/order';
import { SelectField, TextField, Txt } from '@app/components/common';
import DateField from '@app/components/common/date-field';
import { useAlert, useApi } from '@app/hooks';

export const OrderForm: FC<{
  order: Order;
  canChange: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}> = ({ order, canChange, onSubmit, onCancel }) => {
  const api = useApi();
  const { error, success } = useAlert();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<OrderDetail>();
  const [statuses, setStatuses] = useState<OrderStatus[]>([]);
  const [statusId, setStatusId] = useState<number>(order.status_id);
  const [comment, setComment] = useState(order.comment || '');
  const [shippingDate, setShippingDate] = useState(order.shipping_date || '');

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [detailRes, statusesRes] = await Promise.all([
        api.orders.getOrder(order.pid),
        api.orders.getStatuses({
          limit: 100,
          offset: 0,
          sort: '+name',
          filter: { is_active: true },
        }),
      ]);
      if (detailRes?.data) {
        setDetail(detailRes.data);
        setStatusId(detailRes.data.status_id);
        setComment(detailRes.data.comment || '');
        setShippingDate(detailRes.data.shipping_date || '');
      }
      setStatuses(statusesRes?.data?.data || []);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.pid]);

  const onSave = async () => {
    setSaving(true);
    try {
      const response = await api.orders.updateOrder(order.pid, {
        status_id: statusId,
        comment,
        shipping_date: shippingDate || undefined,
      });
      if (response?.code === 200) {
        success('Заказ обновлён');
        onSubmit();
      }
    } catch (e) {
      error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !detail) {
    return (
      <Box minH={200} display="flex" alignItems="center" justifyContent="center">
        <Spinner />
      </Box>
    );
  }

  const selectedStatus = statuses.find((item) => item.id === statusId);

  return (
    <Flex gap={4} flexDirection="column">
      <Flex gap={4} wrap="wrap">
        <Txt color="gray.500">Дистрибьютор: {detail.distributor_name || detail.distributor_id}</Txt>
        <Txt color="gray.500">ТТ: {detail.outlet_id}</Txt>
        <Txt color="gray.500">
          Сумма: {detail.total_amount != null ? detail.total_amount.toLocaleString('ru-RU') : '—'}
        </Txt>
      </Flex>

      <SelectField
        id="status_id"
        label="Статус"
        disabled={!canChange}
        options={statuses}
        value={selectedStatus}
        onChange={(option) => setStatusId(option ? Number(option.id) : statusId)}
      />
      <DateField
        id="shipping_date"
        label="Дата отгрузки"
        disabled={!canChange}
        value={shippingDate}
        onChange={setShippingDate}
      />
      <TextField
        id="comment"
        label="Комментарий"
        multiline
        rows={3}
        disabled={!canChange}
        value={comment}
        onChange={setComment}
      />

      <Txt fontWeight={500}>Позиции заказа</Txt>
      <Box overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>SKU</Th>
              <Th>Ед. изм.</Th>
              <Th isNumeric>Кол-во</Th>
              <Th isNumeric>Цена</Th>
              <Th isNumeric>Сумма</Th>
            </Tr>
          </Thead>
          <Tbody>
            {(detail.items || []).map((item) => (
              <Tr key={item.pid}>
                <Td>{item.sku_name || item.sku_id}</Td>
                <Td>{item.unit_sku_name || item.unit_sku_id || '—'}</Td>
                <Td isNumeric>{item.number}</Td>
                <Td isNumeric>{item.price?.toLocaleString('ru-RU')}</Td>
                <Td isNumeric>{(item.number * item.price).toLocaleString('ru-RU')}</Td>
              </Tr>
            ))}
            {(!detail.items || detail.items.length === 0) && (
              <Tr>
                <Td colSpan={5}>
                  <Txt color="gray.500" textAlign="center">Нет позиций</Txt>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      <Flex justifyContent="flex-end" gap={2}>
        <Button variant="outline" colorScheme="gray" onClick={onCancel} isDisabled={saving}>
          {canChange ? 'Отмена' : 'Закрыть'}
        </Button>
        {canChange && (
          <Button variant="solid" colorScheme="brand" onClick={onSave} isLoading={saving}>
            Сохранить
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export default OrderForm;
